import { useSetDefaultWallet } from '@/backend/user/wallet/post-set-default';
import { getIcon } from '@/Icons';
import {
	useSettingsStoreActions,
	useShowFiat,
	useUserWallets,
	useZeroBalancesHidden,
} from '@/store';
import { convertToFiat } from '@/utils/convertToFiat';
import React, { forwardRef, useState } from 'react';
import ds from '@/styles/ds/ds.module.scss';
import Button_Toggle from '@/components/layouts/SideNav/components/Buttons/Button_Toggle/Button_Toggle';
import Decimal from 'decimal.js';
import { formatDecimal } from '@/utils/formatDecimal';

interface WalletCurrencyDropdownProps {
	closeModal: () => void;
}

const WalletCurrencyDropdown = forwardRef<
	HTMLDivElement,
	WalletCurrencyDropdownProps
>(({ closeModal }, ref) => {
	const userWallets = useUserWallets();
	const { setDefaultWallet } = useSetDefaultWallet();
	const showFiat = useShowFiat();
	const zeroBalancesHidden = useZeroBalancesHidden();
	const { setShowFiat, setZeroBalancesHidden } = useSettingsStoreActions();

	const toggleFiat = () => setShowFiat(!showFiat);

	const filteredWallets = userWallets?.filter((wallet) => {
		if (!zeroBalancesHidden) return true;
		return (
			new Decimal(wallet.amount).greaterThan(0) ||
			new Decimal(wallet.bonusAmount).greaterThan(0)
		);
	});

	return (
		<div
			ref={ref}
			className="rounded-[8px] bg-midnight-blue absolute right-0 md:right-[79.5px] top-[70px] 
			max-h-[500px] grid grid-rows-2 px-4 md:min-w-[320px] w-[100vw] md:w-[320px]"
			style={{ gridTemplateRows: '1fr min-content' }}
		>
			<div
				className={`flex flex-col gap-3 overflow-auto pt-2 ${ds.scrollbar_thin}`}
			>
				{filteredWallets?.map((wallet) => (
					<div
						key={wallet.id}
						className="flex flex-col  rounded-[8px] bg-teal-blue"
					>
						<button
							aria-label="Select Wallet"
							className="flex gap-4 justify-between items-center w-full font-bold  !p-2"
							onClick={async (e) => {
								e.stopPropagation();
								const data = await setDefaultWallet({ wallet });
								if (data?.success) closeModal();
							}}
						>
							<span className="flex gap-2 items-center">
								{getIcon(wallet.currency.code)}
								<>{wallet.currency.code}</>
							</span>
							{showFiat
								? `€${convertToFiat({
										amount: wallet.amount,
										exchangeRate: wallet?.currency?.exchangeRate,
									})}`
								: `${formatDecimal({
										value: new Decimal(wallet.amount),
										exchangeRate: wallet?.currency?.exchangeRate,
									})}`}
						</button>

						{new Decimal(wallet.bonusAmount).greaterThan(0) && (
							<button
								aria-label="Select Bonus Wallet"
								className="flex gap-4 justify-between items-center w-full font-bold opacity-70 !p-2"
								onClick={async (e) => {
									e.stopPropagation();
									const data = await setDefaultWallet({ wallet });
									if (data?.success) closeModal();
								}}
							>
								<span className="flex gap-2 items-center whitespace-nowrap">
									{getIcon(wallet.currency.code)}
									<>
										{wallet.currency.code} BONUS
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="14"
											height="15"
											viewBox="0 0 14 15"
											fill="none"
										>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M10.7332 6.56484H11.1999C11.9731 6.56484 12.5999 7.19165 12.5999 7.96484V12.6315C12.5999 13.4047 11.9731 14.0315 11.1999 14.0315H2.7999C2.0267 14.0315 1.3999 13.4047 1.3999 12.6315V7.96484C1.3999 7.19165 2.0267 6.56484 2.7999 6.56484H3.26657V4.34818C3.26657 2.43131 4.98919 0.964844 6.9999 0.964844C9.01061 0.964844 10.7332 2.43131 10.7332 4.34818V6.56484ZM4.1999 4.34818V6.56484H9.7999V4.34818C9.7999 3.04342 8.59742 1.89818 6.9999 1.89818C5.40239 1.89818 4.1999 3.04342 4.1999 4.34818ZM2.33324 12.6315C2.33324 12.8892 2.54217 13.0982 2.7999 13.0982H11.1999C11.4576 13.0982 11.6666 12.8892 11.6666 12.6315V7.96484C11.6666 7.70711 11.4576 7.49818 11.1999 7.49818H2.7999C2.54217 7.49818 2.33324 7.70711 2.33324 7.96484V12.6315Z"
												fill="currentColor"
											/>
										</svg>
									</>
								</span>
								{showFiat
									? `€${convertToFiat({
											amount: wallet.bonusAmount,
											exchangeRate: wallet?.currency?.exchangeRate,
										})}`
									: `${formatDecimal({
											value: new Decimal(wallet.bonusAmount),
											exchangeRate: wallet?.currency?.exchangeRate,
										})}`}
							</button>
						)}
					</div>
				))}
			</div>

			<div className="py-2.5 pt-5 flex flex-col gap-5">
				<div className="w-full flex justify-between gap-2 items-center">
					<p className="text-xs font-bold">Hide zero balances</p>
					<Button_Toggle
						isOn={zeroBalancesHidden}
						toggle={() => setZeroBalancesHidden(!zeroBalancesHidden)}
					/>
				</div>
				<div className="w-full flex justify-between gap-2 items-center">
					<p className="text-xs font-bold">Display in fiat</p>
					<Button_Toggle isOn={showFiat} toggle={toggleFiat} />
				</div>
			</div>
		</div>
	);
});

WalletCurrencyDropdown.displayName = 'WalletCurrencyDropdown';

export default WalletCurrencyDropdown;
